import Image, { type StaticImageData } from "next/image"
import Link from "next/link"
import type { FC, HTMLAttributes } from "react"
import { config } from "@/config"
import logoIconDarkImg from "@/public/logos/logo-icon--dark.png"
import logoIconLightImg from "@/public/logos/logo-icon--light.png"
import logoIconPrimaryImg from "@/public/logos/logo-icon--primary.png"
import { cn } from "@/utils/cn"

type LogoVariant = "primary" | "light" | "dark"

const LOGOS: Record<LogoVariant, StaticImageData> = {
  primary: logoIconPrimaryImg,
  light: logoIconLightImg,
  dark: logoIconDarkImg,
}

interface LogoProps extends HTMLAttributes<HTMLDivElement> {
  variant?: LogoVariant
  size?: number
  asH1?: boolean
  noLink?: boolean
  noText?: boolean
}

export const Logo: FC<LogoProps> = ({ noLink = false, ...props }) => {
  return noLink ? (
    <LogoInner {...props} />
  ) : (
    <Link href="/" className="group outline-hidden transition-opacity hover:opacity-90">
      <LogoInner {...props} />
    </Link>
  )
}

function LogoInner({
  variant = "primary",
  size = 36,
  asH1 = false,
  noText = false,
  className,
  ...props
}: Omit<LogoProps, "noLink">) {
  const Text = asH1 ? "h1" : "span"

  return (
    <div className={cn("flex select-none items-center gap-2", className)} {...props}>
      <Image
        src={LOGOS[variant]}
        height={size}
        width={size}
        className={cn("block shrink-0 shadow-md")}
        style={{
          borderRadius: `${(size * 13) / 36}px`,
        }}
        alt={`${config.site.name} Logo`}
        priority
      />

      {!noText && (
        <Text
          className={cn(
            "shrink-0 whitespace-nowrap font-display text-[20px] text-foreground leading-none ",
          )}
        >
          <span className="font-extrabold">Backlink</span>
          <span className="font-medium">GPT</span>
        </Text>
      )}
    </div>
  )
}

export const LogoWatermark = ({ className, ...props }: LogoProps) => {
  return (
    <Logo
      className={cn(
        "opacity-15 mix-blend-multiply saturate-0 dark:mix-blend-difference",
        className,
      )}
      noLink
      {...props}
    />
  )
}
